import { addDays, addYears, format, parseISO, subMonths } from 'date-fns'

// SINGLE SOURCE for TAEPP (trainer) CPD + practice-hour tracking. Mirrored by
// supabase/functions/send-taepp-reminders — keep the two in lock-step.
//
// Each trainer has their OWN 2-year membership cycle (auditors.taepp_cycle_start / taepp_cycle_end, inclusive
// dates). Within the cycle they need:
//   • 40 CPD hours from NAE Registry programmes, of which at least 10 from IAL programmes (IAL counts toward
//     BOTH the 40 and the 10), and
//   • 80 practice hours (delivery / curriculum development / assessment work).
// A course or practice entry counts in the cycle in which it FINISHED (date_end, else date_start).
//
// TAEPP courses are logged in the ordinary training ledger (auditor_training) with taepp_type 'NAE' | 'IAL';
// they are always saved with cpd_type 'Non-accredited', so the SAC CPD calculation reads them unchanged.

export const TAEPP_REQ = { nae: 40, ial: 10, practice: 80 }

export const TAEPP_LABEL = { NAE: 'TAEPP – NAE Registry', IAL: 'TAEPP – IAL' }

export const PRACTICE_ROLES = ['Trainer', 'Facilitator', 'Assessor', 'Designer', 'Consultant', 'Other']
export const PRACTICE_AREAS = ['Delivery', 'Curriculum Development', 'Assessment']
// Picking a role pre-fills the obvious area (still editable). Consultant / Other are left for the user to choose.
export const ROLE_AREA = { Trainer: 'Delivery', Facilitator: 'Delivery', Assessor: 'Assessment', Designer: 'Curriculum Development' }

// A person whose ONLY role is Trainer (e.g. freelance TAEPP trainers) — not part of the AO scheme, so auditor
// checks, audit log, competency and observations do not apply to them.
export const isTrainerOnly = a => !!a?.is_trainer && !a.is_auditor && !a.is_decision_maker && !a.is_staff

const iso = d => format(d, 'yyyy-MM-dd')

// Cycle end = start + 2 years − 1 day (e.g. 10 Apr 2026 → 9 Apr 2028).
export function taeppCycleEnd(start) {
  if (!start) return ''
  try { return iso(addDays(addYears(parseISO(start), 2), -1)) } catch (e) { return '' }
}
export function nextCycleStart(end) {
  if (!end) return ''
  try { return iso(addDays(parseISO(end), 1)) } catch (e) { return '' }
}

// The trainer's current cycle, or null when no start date is set.
export function currentTaeppCycle(a) {
  if (!a?.taepp_cycle_start) return null
  return { start: a.taepp_cycle_start, end: a.taepp_cycle_end || taeppCycleEnd(a.taepp_cycle_start) }
}
// Current cycle first, then past cycles (newest first).
export function allTaeppCycles(a) {
  const cur = currentTaeppCycle(a)
  const past = (Array.isArray(a?.taepp_cycle_history) ? a.taepp_cycle_history : [])
    .filter(c => c && c.start && c.end)
    .sort((x, y) => String(y.start).localeCompare(String(x.start)))
  return [...(cur ? [cur] : []), ...past]
}

const finishDate = r => String(r.date_end || r.date_start || '').slice(0, 10)
export const inTaeppCycle = (r, cycle) => {
  const d = finishDate(r)
  return !!d && !!cycle?.start && !!cycle?.end && d >= cycle.start && d <= cycle.end
}

export function computeTaepp(training, practice, cycle) {
  if (!cycle?.start || !cycle?.end) return null
  const sum = rs => rs.reduce((s, r) => s + (Number(r.hours) || 0), 0)
  const courses = (training || []).filter(r => (r.taepp_type === 'NAE' || r.taepp_type === 'IAL') && inTaeppCycle(r, cycle))
  const nae = sum(courses)                                        // IAL is on the NAE Registry → counts here too
  const ial = sum(courses.filter(r => r.taepp_type === 'IAL'))
  const prac = sum((practice || []).filter(r => inTaeppCycle(r, cycle)))
  const naeShort = Math.max(0, TAEPP_REQ.nae - nae)
  const ialShort = Math.max(0, TAEPP_REQ.ial - ial)
  const practiceShort = Math.max(0, TAEPP_REQ.practice - prac)
  return { nae, ial, practice: prac, naeShort, ialShort, practiceShort, met: !naeShort && !ialShort && !practiceShort }
}

// Within the last 6 months of the cycle (and not yet past its end) — when a shortfall needs attention.
export function taeppInFinalSixMonths(cycle, today = new Date()) {
  if (!cycle?.end) return false
  const t = iso(today)
  return t >= iso(subMonths(parseISO(cycle.end), 6)) && t <= cycle.end
}

export const fmtHrs = n => (Number(n) || 0).toFixed(1).replace(/\.0$/, '')
